/**
 * Doserne Academy — Agreement signing page (documents to sign).
 * Route: /doserne/avtale/:code
 *
 * Shows a published agreement (e.g. "Averaging of working hours") that any
 * member may CHOOSE to sign. Reuses the same checkbox + typed name +
 * handwritten signature UX as the course acknowledgement.
 */
import React, { useEffect, useMemo, useRef, useState } from "react";
import { useParams, Link } from "react-router-dom";
import axios from "axios";
import { CheckCircle2, PenSquare, RotateCcw, ShieldCheck, Loader2, ArrowLeft, FileText, Download } from "lucide-react";
import DoserneShell from "./DoserneShell";
import { useLang } from "./i18n";
import { useDoserneAuth } from "./doserneAuth";
import PdfViewerModal from "./PdfViewerModal";
import LangFlag from "./LangFlag";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const LANG_NAMES = { no: "Norsk", ro: "Română", en: "English", pl: "Polski" };

const T = {
  no: { back: "Tilbake til Kurs", consent: "Jeg bekrefter", nameLabel: "Fullt navn", namePh: "F.eks. Kari Nordmann",
    sigLabel: "Håndskrevet signatur", sigHint: "Bruk finger eller mus for å signere.", clear: "Tøm",
    submit: "Signer dokumentet", submitting: "Signerer …", done: "Dokumentet er signert", viewPdf: "Åpne signert PDF (PDF)",
    error: "Kunne ikke signere. Prøv igjen.", optional: "Signering er frivillig",
    periodLabel: "Avtaleperiode", periodFrom: "Fra", periodTo: "Til" },
  ro: { back: "Înapoi la Kurs", consent: "Confirm", nameLabel: "Nume complet", namePh: "Ex. Ionescu Andrei",
    sigLabel: "Semnătură olografă", sigHint: "Folosește degetul sau mouse-ul pentru a semna.", clear: "Șterge",
    submit: "Semnează documentul", submitting: "Se semnează …", done: "Documentul a fost semnat", viewPdf: "Deschide PDF-ul semnat",
    error: "Nu s-a putut semna. Încearcă din nou.", optional: "Semnarea este opțională",
    periodLabel: "Perioada acordului", periodFrom: "De la", periodTo: "Până la" },
  en: { back: "Back to Courses", consent: "I confirm", nameLabel: "Full name", namePh: "e.g. John Anderson",
    sigLabel: "Handwritten signature", sigHint: "Use your finger or mouse to sign.", clear: "Clear",
    submit: "Sign the document", submitting: "Signing …", done: "The document is signed", viewPdf: "Open signed PDF",
    error: "Could not sign. Please try again.", optional: "Signing is optional",
    periodLabel: "Agreement period", periodFrom: "From", periodTo: "To" },
};

function SignaturePad({ onChange, resetKey }) {
  const canvasRef = useRef(null);
  const drawing = useRef(false);
  const last = useRef({ x: 0, y: 0 });
  const hasInk = useRef(false);

  const scale = () => {
    const c = canvasRef.current;
    if (!c) return;
    const rect = c.getBoundingClientRect();
    const dpr = window.devicePixelRatio || 1;
    c.width = Math.max(1, Math.floor(rect.width * dpr));
    c.height = Math.max(1, Math.floor(rect.height * dpr));
    const ctx = c.getContext("2d");
    ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
    ctx.strokeStyle = "#0f172a";
    ctx.lineWidth = 2.4;
    ctx.lineCap = "round";
    ctx.lineJoin = "round";
    ctx.fillStyle = "#ffffff";
    ctx.fillRect(0, 0, rect.width, rect.height);
  };

  useEffect(() => {
    scale();
    hasInk.current = false;
    onChange(null);
    const on = () => scale();
    window.addEventListener("resize", on);
    return () => window.removeEventListener("resize", on);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [resetKey]);

  const getPoint = (e) => {
    const c = canvasRef.current;
    const rect = c.getBoundingClientRect();
    const tt = e.touches ? e.touches[0] : e;
    return { x: tt.clientX - rect.left, y: tt.clientY - rect.top };
  };
  const start = (e) => { e.preventDefault(); drawing.current = true; last.current = getPoint(e); };
  const move = (e) => {
    if (!drawing.current) return;
    e.preventDefault();
    const p = getPoint(e);
    const ctx = canvasRef.current.getContext("2d");
    ctx.beginPath(); ctx.moveTo(last.current.x, last.current.y); ctx.lineTo(p.x, p.y); ctx.stroke();
    last.current = p; hasInk.current = true;
  };
  const end = () => {
    if (!drawing.current) return;
    drawing.current = false;
    if (hasInk.current) onChange(canvasRef.current.toDataURL("image/png"));
  };

  useEffect(() => {
    const c = canvasRef.current;
    if (!c) return;
    const onDown = (e) => start(e); const onMove = (e) => move(e); const onUp = () => end();
    c.addEventListener("pointerdown", onDown); c.addEventListener("pointermove", onMove);
    c.addEventListener("pointerup", onUp); c.addEventListener("pointerleave", onUp);
    c.addEventListener("mousedown", onDown); c.addEventListener("mousemove", onMove); c.addEventListener("mouseup", onUp);
    c.addEventListener("touchstart", onDown, { passive: false }); c.addEventListener("touchmove", onMove, { passive: false }); c.addEventListener("touchend", onUp);
    return () => {
      c.removeEventListener("pointerdown", onDown); c.removeEventListener("pointermove", onMove);
      c.removeEventListener("pointerup", onUp); c.removeEventListener("pointerleave", onUp);
      c.removeEventListener("mousedown", onDown); c.removeEventListener("mousemove", onMove); c.removeEventListener("mouseup", onUp);
      c.removeEventListener("touchstart", onDown); c.removeEventListener("touchmove", onMove); c.removeEventListener("touchend", onUp);
    };
  }, []);

  return <canvas ref={canvasRef} data-testid="agreement-signature-pad" className="w-full h-40 sm:h-48 rounded-2xl border-2 border-dashed border-slate-300 bg-white touch-none" />;
}

export default function DoserneAgreementSign() {
  const { code } = useParams();
  const { lang } = useLang();
  const { user, loading, authHeader } = useDoserneAuth();
  const t = T[lang] || T.no;

  const [agr, setAgr] = useState(null);
  const [busy, setBusy] = useState(true);
  const [checked, setChecked] = useState(false);
  const [typedName, setTypedName] = useState("");
  const [periodFrom, setPeriodFrom] = useState("");
  const [periodTo, setPeriodTo] = useState("");
  const [signature, setSignature] = useState(null);
  const [resetKey, setResetKey] = useState(0);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState("");
  const [signed, setSigned] = useState(null); // signature id when done
  const [pdfLang, setPdfLang] = useState(null);

  const load = async () => {
    try {
      const { data } = await axios.get(`${API}/doserne/agreements/${code}?lang=${lang}`, authHeader());
      setAgr(data);
      if (data.signed) setSigned(data.signature_id);
    } catch { setError(t.error); }
    setBusy(false);
  };
  useEffect(() => {
    if (!user) return;
    if (typedName === "") setTypedName(user.full_name || "");
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user, code, lang]);

  const canSubmit = useMemo(
    () => checked && typedName.trim().length >= 2 && (!agr?.requires_period || (!!periodFrom && !!periodTo)) && !!signature && !submitting,
    [checked, typedName, periodFrom, periodTo, signature, submitting, agr]
  );

  const submit = async () => {
    if (!canSubmit) return;
    setSubmitting(true); setError("");
    try {
      const { data } = await axios.post(
        `${API}/doserne/agreements/${code}/sign`,
        { accepted: true, typed_name: typedName.trim(), signature_data_url: signature, lang, period_from: periodFrom, period_to: periodTo },
        authHeader()
      );
      setSigned(data.signature?.id || true);
    } catch (e) {
      setError(e?.response?.data?.detail || t.error);
    } finally { setSubmitting(false); }
  };

  if (loading || busy) {
    return <DoserneShell><div className="max-w-3xl mx-auto p-6"><Loader2 className="animate-spin text-slate-400" /></div></DoserneShell>;
  }
  if (!agr) {
    return <DoserneShell><div className="max-w-3xl mx-auto p-6 text-slate-500">{t.error}</div></DoserneShell>;
  }

  return (
    <DoserneShell>
      <div className="max-w-3xl mx-auto p-4 sm:p-6" data-testid="agreement-sign-page">
        <Link to="/doserne/kurs" className="inline-flex items-center gap-2 text-slate-600 hover:text-slate-900 text-sm" data-testid="agreement-back-link">
          <ArrowLeft className="w-4 h-4" /> {t.back}
        </Link>

        <div className="mt-6 rounded-3xl bg-gradient-to-br from-emerald-900 to-emerald-800 text-white p-6 sm:p-8">
          <div className="text-3xl mb-2">{agr.icon || "📄"}</div>
          <h1 className="text-2xl sm:text-3xl font-black leading-tight">{agr.title}</h1>
          <p className="mt-2 text-emerald-100 text-sm sm:text-base">{agr.subtitle}</p>
        </div>

        <div className="mt-6 rounded-3xl bg-white border border-slate-200 p-6 shadow-sm">
          <p className="text-slate-700 leading-relaxed mb-4">{agr.intro}</p>
          {(agr.sections || []).map((s, i) => (
            <div key={i} className="mb-4">
              <div className="font-bold text-slate-900 mb-1">{s.h}</div>
              <p className="text-slate-700 leading-relaxed text-sm">{s.p}</p>
            </div>
          ))}
        </div>

        {signed ? (
          <div className="mt-6 rounded-3xl bg-white border border-emerald-200 p-8 text-center shadow-sm" data-testid="agreement-done">
            <div className="inline-flex items-center justify-center w-16 h-16 rounded-full bg-emerald-100 text-emerald-600 mb-4">
              <CheckCircle2 className="w-8 h-8" />
            </div>
            <div className="text-lg font-black text-slate-900 mb-4">{t.done}</div>
            <div className="flex flex-wrap items-center justify-center gap-2" data-testid="agreement-lang-list">
              {(agr.langs && agr.langs.length ? agr.langs : [agr.signed_lang || lang]).map((lg) => (
                <button
                  key={lg}
                  type="button"
                  onClick={() => setPdfLang(lg)}
                  className={`inline-flex items-center gap-2 px-4 py-2.5 rounded-2xl text-sm font-bold border-2 ${lg === (agr.signed_lang || lang) ? "border-emerald-600 bg-emerald-600 text-white" : "border-slate-200 text-slate-700 bg-white hover:border-emerald-400"}`}
                  data-testid={`agreement-lang-${lg}`}
                >
                  <LangFlag lang={lg} size={20} /> {LANG_NAMES[lg] || lg.toUpperCase()}
                </button>
              ))}
            </div>
          </div>
        ) : (
          <>
            <div className="mt-6 rounded-3xl bg-white border border-slate-200 p-6 shadow-sm">
              <label className="flex items-start gap-3 cursor-pointer select-none">
                <input type="checkbox" checked={checked} onChange={(e) => setChecked(e.target.checked)}
                  className="mt-1 w-5 h-5 rounded border-slate-300 text-emerald-600 focus:ring-emerald-500" data-testid="agreement-consent-checkbox" />
                <span className="text-sm font-medium text-slate-900">{agr.consent}</span>
              </label>
            </div>

            <div className="mt-6 rounded-3xl bg-white border border-slate-200 p-6 shadow-sm">
              <label className="block text-xs font-bold uppercase tracking-[0.25em] text-slate-500 mb-2">{t.nameLabel}</label>
              <input type="text" value={typedName} onChange={(e) => setTypedName(e.target.value)} placeholder={t.namePh}
                className="w-full px-4 py-3 rounded-2xl border-2 border-slate-200 focus:border-emerald-500 outline-none text-base" data-testid="agreement-typed-name" />
            </div>

            <div className="mt-6 rounded-3xl bg-white border border-slate-200 p-6 shadow-sm" style={{ display: agr.requires_period ? undefined : "none" }}>
              <label className="block text-xs font-bold uppercase tracking-[0.25em] text-slate-500 mb-3">{t.periodLabel}</label>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <span className="block text-xs font-semibold text-slate-500 mb-1">{t.periodFrom}</span>
                  <input type="month" value={periodFrom} onChange={(e) => setPeriodFrom(e.target.value)}
                    className="w-full px-3 py-3 rounded-2xl border-2 border-slate-200 focus:border-emerald-500 outline-none text-base" data-testid="agreement-period-from" />
                </div>
                <div>
                  <span className="block text-xs font-semibold text-slate-500 mb-1">{t.periodTo}</span>
                  <input type="month" value={periodTo} onChange={(e) => setPeriodTo(e.target.value)}
                    className="w-full px-3 py-3 rounded-2xl border-2 border-slate-200 focus:border-emerald-500 outline-none text-base" data-testid="agreement-period-to" />
                </div>
              </div>
            </div>

            <div className="mt-6 rounded-3xl bg-white border border-slate-200 p-6 shadow-sm">
              <div className="flex items-center justify-between mb-2">
                <label className="text-xs font-bold uppercase tracking-[0.25em] text-slate-500 inline-flex items-center gap-2">
                  <PenSquare className="w-4 h-4" /> {t.sigLabel}
                </label>
                <button type="button" onClick={() => { setResetKey((k) => k + 1); setSignature(null); }}
                  className="inline-flex items-center gap-1.5 text-xs font-bold text-slate-500 hover:text-slate-900" data-testid="agreement-clear-signature">
                  <RotateCcw className="w-3.5 h-3.5" /> {t.clear}
                </button>
              </div>
              <SignaturePad onChange={setSignature} resetKey={resetKey} />
              <div className="mt-2 text-xs text-slate-500">{t.sigHint}</div>
            </div>

            {error && <div className="mt-4 text-sm text-red-600 font-semibold" data-testid="agreement-error">{error}</div>}

            <div className="mt-6">
              <button type="button" onClick={submit} disabled={!canSubmit}
                className={"w-full inline-flex items-center justify-center gap-2 px-6 py-4 rounded-2xl font-black text-base shadow " +
                  (canSubmit ? "bg-emerald-600 hover:bg-emerald-700 text-white" : "bg-slate-200 text-slate-400 cursor-not-allowed")}
                data-testid="agreement-submit-btn">
                {submitting ? <Loader2 className="w-5 h-5 animate-spin" /> : <ShieldCheck className="w-5 h-5" />}
                {submitting ? t.submitting : t.submit}
              </button>
              <div className="mt-2 text-center text-xs text-slate-400 inline-flex items-center gap-1 w-full justify-center"><FileText className="w-3 h-3" /> {t.optional}</div>
            </div>
          </>
        )}
      </div>

      {pdfLang && (
        <PdfViewerModal
          url={`${API}/doserne/agreements/${code}/pdf?lang=${pdfLang}`}
          authHeader={authHeader}
          filename={`Doserne_${code}_${pdfLang.toUpperCase()}.pdf`}
          title={`${agr.title} · ${LANG_NAMES[pdfLang] || pdfLang.toUpperCase()}`}
          labels={{ download: t.viewPdf, close: t.back }}
          onClose={() => setPdfLang(null)}
        />
      )}
    </DoserneShell>
  );
}
